import React, { useState, useEffect, useRef } from 'react';
import { Input, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import { vehiclesAPI } from '../../helpers/api';
import { openAuthFile } from '../../components/Common/AuthImage';
import { useIsAdmin } from '../../helpers/hooks';

interface Vehicle {
  id: string;
  plate: string;
  brand: string | null;
  model: string | null;
  type: string;
  isMachinery: boolean;
  ownerName: string | null;
  dailyRate: number | null;
  isActive: boolean;
}

interface UsageHistoryItem {
  id: string;
  date: string;
  usageType: string;
  notes: string | null;
  km: number | null;
  hours: number | null;
  driverType: string | null;
  driver: string | null;
  site: {
    id: string;
    name: string;
    project: string;
    city: string;
  } | null;
  createdBy: string;
}

interface VehicleDocument {
  id: string;
  documentType: string;
  fileName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
  description: string | null;
  expiryDate: string | null;
  createdAt: string;
}

interface VehicleDetailModalProps {
  isOpen: boolean;
  toggle: () => void;
  vehicle: Vehicle | null;
  onUpdate?: () => void;
}

const documentTypeLabels: Record<string, string> = {
  REGISTRATION: 'Ruhsat',
  INSURANCE: 'Sigorta Poliçesi',
  INSPECTION: 'Muayene',
  OTHER: 'Diğer',
};

const documentTypeIcons: Record<string, string> = {
  REGISTRATION: 'ri-file-text-line',
  INSURANCE: 'ri-shield-check-line',
  INSPECTION: 'ri-check-double-line',
  OTHER: 'ri-file-line',
};

const ownershipLabels: Record<string, string> = {
  COMPANY: 'Şirket',
  RENTAL: 'Kiralık',
};

const ownershipStyles: Record<string, string> = {
  COMPANY: 'bg-primary bg-opacity-10 text-primary',
  RENTAL: 'bg-info bg-opacity-10 text-info',
};

// Helper to get display label for vehicle type
const getVehicleTypeLabel = (vehicle: Vehicle) => {
  const ownership = ownershipLabels[vehicle.type] || vehicle.type;
  if (vehicle.isMachinery) {
    return `${ownership} İş Makinesi`;
  }
  return `${ownership} Araç`;
};

// Helper to get style for vehicle
const getVehicleTypeStyle = (vehicle: Vehicle) => {
  if (vehicle.isMachinery) {
    return 'bg-warning bg-opacity-10 text-warning';
  }
  return ownershipStyles[vehicle.type] || 'bg-secondary bg-opacity-10 text-secondary';
};

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const getUsageTypeLabel = (type: string) => {
  return type === 'FULL_DAY' ? 'Tam Gün' : 'Yarım Gün';
};

const VehicleDetailModal: React.FC<VehicleDetailModalProps> = ({
  isOpen,
  toggle,
  vehicle,
  onUpdate,
}) => {
  const isAdmin = useIsAdmin();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentIsActive, setCurrentIsActive] = useState(true);
  const [formData, setFormData] = useState({
    plate: '',
    brand: '',
    customBrand: '',
    model: '',
    customModel: '',
    type: 'COMPANY',
    isMachinery: false,
    ownerName: '',
    dailyRate: '',
  });

  // Usage history state
  const [usageHistory, setUsageHistory] = useState<UsageHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Brands/Models for autocomplete
  const [brands, setBrands] = useState<string[]>([]);
  const [models, setModels] = useState<string[]>([]);
  const [brandModels, setBrandModels] = useState<Record<string, string[]>>({});

  // Documents state
  const [documents, setDocuments] = useState<VehicleDocument[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadDocType, setUploadDocType] = useState('REGISTRATION');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploadExpiryDate, setUploadExpiryDate] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset form and fetch history when vehicle changes
  useEffect(() => {
    if (vehicle && isOpen) {
      // Will be updated after brands/models are fetched
      setFormData({
        plate: vehicle.plate || '',
        brand: vehicle.brand || '',
        customBrand: '',
        model: vehicle.model || '',
        customModel: '',
        type: vehicle.type || 'COMPANY',
        isMachinery: vehicle.isMachinery || false,
        ownerName: vehicle.ownerName || '',
        dailyRate: vehicle.dailyRate?.toString() || '',
      });
      setCurrentIsActive(vehicle.isActive);
      setIsEditing(false);
      fetchUsageHistory(vehicle.id);
      fetchDocuments(vehicle.id);
    }
  }, [vehicle, isOpen]);

  // Update form data when brands/models are loaded (to handle custom values)
  useEffect(() => {
    if (vehicle && brands.length > 0) {
      const brandInList = brands.includes(vehicle.brand || '');
      const currentModel = vehicle.model || '';
      const availableModels = brandInList && vehicle.brand && brandModels[vehicle.brand]
        ? brandModels[vehicle.brand]
        : models;
      const modelInList = availableModels.includes(currentModel);

      setFormData(prev => ({
        ...prev,
        brand: brandInList ? (vehicle.brand || '') : (vehicle.brand ? '__OTHER__' : ''),
        customBrand: brandInList ? '' : (vehicle.brand || ''),
        model: modelInList ? currentModel : (currentModel ? '__OTHER__' : ''),
        customModel: modelInList ? '' : currentModel,
      }));
    }
  }, [vehicle, brands, models, brandModels]);

  const fetchUsageHistory = async (vehicleId: string) => {
    try {
      setLoadingHistory(true);
      const response = await vehiclesAPI.getUsageHistory(vehicleId);
      setUsageHistory(response.data.usageHistory);
    } catch (error) {
      console.error('Usage history fetch error:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchBrandsAndModels = async () => {
    try {
      const response = await vehiclesAPI.getBrandsAndModels();
      setBrands(response.data.brands);
      setModels(response.data.models);
      setBrandModels(response.data.brandModels);
    } catch (error) {
      console.error('Brands/models fetch error:', error);
    }
  };

  const fetchDocuments = async (vehicleId: string) => {
    try {
      setLoadingDocs(true);
      const response = await vehiclesAPI.getDocuments(vehicleId);
      setDocuments(response.data);
    } catch (error) {
      console.error('Documents fetch error:', error);
    } finally {
      setLoadingDocs(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!vehicle || !selectedFile) return;

    try {
      setUploadingDoc(true);
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('documentType', uploadDocType);
      if (uploadDescription) formData.append('description', uploadDescription);
      if (uploadExpiryDate) formData.append('expiryDate', uploadExpiryDate);

      await vehiclesAPI.uploadDocument(vehicle.id, formData);
      toast.success('Evrak yüklendi');
      setShowUploadForm(false);
      setSelectedFile(null);
      setUploadDocType('REGISTRATION');
      setUploadDescription('');
      setUploadExpiryDate('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchDocuments(vehicle.id);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Evrak yüklenemedi');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteDocument = async (docId: string, docName: string) => {
    if (!vehicle) return;
    if (!window.confirm(`"${docName}" evrakını silmek istediğinize emin misiniz?`)) return;

    try {
      await vehiclesAPI.deleteDocument(docId);
      toast.success('Evrak silindi');
      fetchDocuments(vehicle.id);
    } catch (error: any) {
      toast.error('Evrak silinemedi');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isExpired = (expiryDate: string | null) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const isExpiringSoon = (expiryDate: string | null) => {
    if (!expiryDate) return false;
    const daysUntilExpiry = (new Date(expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
    return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
  };

  // Seçilen markaya göre modelleri filtrele
  const getFilteredModels = () => {
    const selectedBrand = formData.brand === '__OTHER__' ? formData.customBrand : formData.brand;
    if (selectedBrand && brandModels[selectedBrand]) {
      return brandModels[selectedBrand];
    }
    return models;
  };

  // Get actual brand/model values (from dropdown or custom input)
  const getActualBrand = () => formData.brand === '__OTHER__' ? formData.customBrand : formData.brand;
  const getActualModel = () => formData.model === '__OTHER__' ? formData.customModel : formData.model;

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: name === 'plate' ? value.toUpperCase() : value }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!vehicle) return;

    try {
      setSaving(true);
      const actualBrand = getActualBrand();
      const actualModel = getActualModel();

      await vehiclesAPI.update(vehicle.id, {
        plate: formData.plate.toUpperCase(),
        brand: actualBrand || undefined,
        model: actualModel || undefined,
        type: formData.type,
        isMachinery: formData.isMachinery,
        ownerName: formData.ownerName || undefined,
        dailyRate: formData.dailyRate ? parseFloat(formData.dailyRate) : undefined,
      });
      toast.success('Araç güncellendi');
      setIsEditing(false);
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (vehicle && brands.length > 0) {
      const brandInList = brands.includes(vehicle.brand || '');
      const currentModel = vehicle.model || '';
      const availableModels = brandInList && vehicle.brand && brandModels[vehicle.brand]
        ? brandModels[vehicle.brand]
        : models;
      const modelInList = availableModels.includes(currentModel);

      setFormData({
        plate: vehicle.plate || '',
        brand: brandInList ? (vehicle.brand || '') : (vehicle.brand ? '__OTHER__' : ''),
        customBrand: brandInList ? '' : (vehicle.brand || ''),
        model: modelInList ? currentModel : (currentModel ? '__OTHER__' : ''),
        customModel: modelInList ? '' : currentModel,
        type: vehicle.type || 'COMPANY',
        isMachinery: vehicle.isMachinery || false,
        ownerName: vehicle.ownerName || '',
        dailyRate: vehicle.dailyRate?.toString() || '',
      });
    }
    setIsEditing(false);
  };

  const handleToggleStatus = async () => {
    if (!vehicle) return;

    try {
      const newStatus = !currentIsActive;
      await vehiclesAPI.update(vehicle.id, { isActive: newStatus });
      setCurrentIsActive(newStatus);
      toast.success(newStatus ? 'Araç aktif yapıldı' : 'Araç pasif yapıldı');
      onUpdate?.();
    } catch (error: any) {
      toast.error('İşlem başarısız');
    }
  };

  const handleDeleteVehicle = async () => {
    if (!vehicle) return;
    if (!window.confirm('Bu aracı silmek istediğinizden emin misiniz?')) return;
    try {
      await vehiclesAPI.delete(vehicle.id);
      toast.success('Araç silindi');
      toggle();
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Silme başarısız');
    }
  };

  if (!vehicle) return null;

  const headerActions = (
    <>
      {isAdmin && (
        <>
          {!isEditing ? (
            <>
              <button
                type="button"
                className="side-modal-expand-btn me-1"
                onClick={() => {
                  fetchBrandsAndModels();
                  setIsEditing(true);
                }}
                title="Düzenle"
              >
                <i className="ri-edit-line"></i>
              </button>
              <button
                type="button"
                className="side-modal-expand-btn me-1"
                onClick={handleDeleteVehicle}
                title="Sil"
                style={{ color: '#f06548' }}
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="side-modal-expand-btn me-1"
                onClick={handleSave}
                disabled={saving}
                title="Kaydet"
                style={{ color: saving ? '#adb5bd' : '#0ab39c' }}
              >
                {saving ? <Spinner size="sm" /> : <i className="ri-check-line"></i>}
              </button>
              <button
                type="button"
                className="side-modal-expand-btn me-1"
                onClick={handleCancel}
                disabled={saving}
                title="İptal"
              >
                <i className="ri-close-line"></i>
              </button>
            </>
          )}
        </>
      )}
      <button
        type="button"
        className="side-modal-expand-btn"
        onClick={toggleExpand}
        title={isExpanded ? 'Küçült' : 'Genişlet'}
      >
        <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
      </button>
    </>
  );

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title="Araç Detayı"
      size={isExpanded ? 'expanded' : 'lg'}
      headerActions={headerActions}
    >
      <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        {/* Header Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }} className="rounded-3 p-3 mb-3">
          <div className="d-flex align-items-start justify-content-between">
            <div style={{ flex: 1, marginRight: '1rem' }}>
              {isEditing ? (
                <Input
                  type="text"
                  name="plate"
                  value={formData.plate}
                  onChange={handleInputChange}
                  className="form-control-lg mb-2"
                  style={{ fontWeight: 600 }}
                />
              ) : (
                <h4 className="mb-1" style={{ fontWeight: 600 }}>
                  {vehicle.plate}
                </h4>
              )}
              {isEditing ? (
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex gap-2">
                    <Input
                      type="select"
                      value={formData.brand}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData({
                          ...formData,
                          brand: value,
                          customBrand: value === '__OTHER__' ? formData.customBrand : '',
                          model: '',
                          customModel: '',
                        });
                      }}
                      bsSize="sm"
                      style={{ width: '120px' }}
                    >
                      <option value="">Marka</option>
                      {brands.map((brand) => (
                        <option key={brand} value={brand}>{brand}</option>
                      ))}
                      <option value="__OTHER__">Diğer</option>
                    </Input>
                    <Input
                      type="select"
                      value={formData.model}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData({
                          ...formData,
                          model: value,
                          customModel: value === '__OTHER__' ? formData.customModel : '',
                        });
                      }}
                      bsSize="sm"
                      style={{ width: '120px' }}
                    >
                      <option value="">Model</option>
                      {getFilteredModels().map((model) => (
                        <option key={model} value={model}>{model}</option>
                      ))}
                      <option value="__OTHER__">Diğer</option>
                    </Input>
                  </div>
                  {(formData.brand === '__OTHER__' || formData.model === '__OTHER__') && (
                    <div className="d-flex gap-2">
                      {formData.brand === '__OTHER__' && (
                        <Input
                          type="text"
                          value={formData.customBrand}
                          onChange={(e) => setFormData({ ...formData, customBrand: e.target.value })}
                          placeholder="Yeni marka"
                          bsSize="sm"
                          style={{ width: '120px' }}
                        />
                      )}
                      {formData.model === '__OTHER__' && (
                        <Input
                          type="text"
                          value={formData.customModel}
                          onChange={(e) => setFormData({ ...formData, customModel: e.target.value })}
                          placeholder="Yeni model"
                          bsSize="sm"
                          style={{ width: '120px' }}
                        />
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                  {vehicle.brand || '-'} {vehicle.model ? `/ ${vehicle.model}` : ''}
                </p>
              )}
            </div>
            {isEditing ? (
              <div className="d-flex flex-column align-items-end gap-1">
                <Input
                  type="select"
                  name="type"
                  value={formData.type}
                  onChange={handleSelectChange}
                  bsSize="sm"
                  style={{ width: 'auto' }}
                >
                  <option value="COMPANY">Şirket</option>
                  <option value="RENTAL">Kiralık</option>
                </Input>
                <label className="d-flex align-items-center gap-1 small" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.isMachinery}
                    onChange={(e) => setFormData((prev) => ({ ...prev, isMachinery: e.target.checked }))}
                  />
                  <i className="ri-tools-line"></i>
                  İş Makinesi
                </label>
              </div>
            ) : (
              <span
                className={`px-2 py-1 rounded-pill small ${getVehicleTypeStyle(vehicle)}`}
                style={{ fontWeight: 500 }}
              >
                {getVehicleTypeLabel(vehicle)}
              </span>
            )}
          </div>
        </div>

        {/* Details Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f06548' }} className="rounded-3 p-3 mb-3">
          <div className="text-muted small mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
            Detaylar
          </div>
          <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
            {/* Durum */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Durum</span>
              <div className="d-flex align-items-center gap-2">
                <span
                  className={`px-2 py-1 rounded-pill small ${
                    currentIsActive
                      ? 'bg-success bg-opacity-10 text-success'
                      : 'bg-secondary bg-opacity-10 text-secondary'
                  }`}
                  style={{ fontWeight: 500 }}
                >
                  {currentIsActive ? 'Aktif' : 'Pasif'}
                </span>
                {isAdmin && !isEditing && (
                  <button
                    type="button"
                    className="btn btn-sm btn-link p-0 text-muted"
                    onClick={handleToggleStatus}
                    title={currentIsActive ? 'Pasif Yap' : 'Aktif Yap'}
                  >
                    <i className="ri-refresh-line"></i>
                  </button>
                )}
              </div>
            </div>
            {/* Sahibi / Kiralama Firması - Şirket aracı hariç */}
            {(isEditing ? formData.type : vehicle.type) !== 'COMPANY' && (
              <div
                className="d-flex align-items-center justify-content-between px-3 py-2"
                style={{ borderBottom: (isEditing ? formData.type : vehicle.type) === 'RENTAL' ? '1px solid #e9ecef' : 'none' }}
              >
                <span className="text-muted">Sahibi / Firma</span>
                {isEditing ? (
                  <Input
                    type="text"
                    name="ownerName"
                    value={formData.ownerName}
                    onChange={handleInputChange}
                    placeholder="Sahibi"
                    bsSize="sm"
                    style={{ width: '150px' }}
                  />
                ) : (
                  <span style={{ fontWeight: 500 }}>{vehicle.ownerName || '-'}</span>
                )}
              </div>
            )}
            {/* Günlük Ücret - Sadece kiralık araçlar için */}
            {(isEditing ? formData.type : vehicle.type) === 'RENTAL' && (
              <div className="d-flex align-items-center justify-content-between px-3 py-2">
                <span className="text-muted">Günlük Ücret</span>
                {isEditing ? (
                  <Input
                    type="number"
                    name="dailyRate"
                    value={formData.dailyRate}
                    onChange={handleInputChange}
                    placeholder="₺"
                    bsSize="sm"
                    style={{ width: '100px' }}
                    min={0}
                  />
                ) : (
                  <span style={{ fontWeight: 500, color: vehicle.dailyRate ? '#405189' : undefined }}>
                    {vehicle.dailyRate ? `₺${vehicle.dailyRate.toLocaleString('tr-TR')}` : '-'}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Documents Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #299cdb' }} className="rounded-3 p-3 mb-3">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              Evraklar
            </span>
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">{documents.length} evrak</span>
              {isAdmin && !isEditing && (
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 text-primary"
                  onClick={() => setShowUploadForm(!showUploadForm)}
                  title="Evrak Ekle"
                >
                  <i className={showUploadForm ? 'ri-close-line' : 'ri-add-line'}></i>
                </button>
              )}
            </div>
          </div>

          {/* Upload Form */}
          {showUploadForm && (
            <div className="rounded-3 p-3 mb-3" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
              <div className="d-flex flex-column gap-2">
                <Input
                  type="select"
                  value={uploadDocType}
                  onChange={(e) => setUploadDocType(e.target.value)}
                  bsSize="sm"
                >
                  <option value="REGISTRATION">Ruhsat</option>
                  <option value="INSURANCE">Sigorta Poliçesi</option>
                  <option value="INSPECTION">Muayene</option>
                  <option value="OTHER">Diğer</option>
                </Input>
                <Input
                  type="text"
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="Açıklama (opsiyonel)"
                  bsSize="sm"
                />
                {(uploadDocType === 'INSURANCE' || uploadDocType === 'INSPECTION') && (
                  <div>
                    <label className="form-label small text-muted mb-1">Son Geçerlilik Tarihi</label>
                    <Input
                      type="date"
                      value={uploadExpiryDate}
                      onChange={(e) => setUploadExpiryDate(e.target.value)}
                      bsSize="sm"
                    />
                  </div>
                )}
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    className="form-control form-control-sm"
                  />
                  <small className="text-muted">PDF, JPG, PNG (max 20MB)</small>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleUploadDocument}
                  disabled={!selectedFile || uploadingDoc}
                >
                  {uploadingDoc ? <Spinner size="sm" /> : <><i className="ri-upload-2-line me-1"></i>Yükle</>}
                </button>
              </div>
            </div>
          )}

          {/* Document List */}
          {loadingDocs ? (
            <div className="text-center py-4">
              <Spinner size="sm" color="primary" />
              <p className="mb-0 mt-2 text-muted small">Yükleniyor...</p>
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-4 rounded-3" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
              <i className="ri-folder-line text-muted" style={{ fontSize: '2rem' }}></i>
              <p className="mb-0 mt-2 text-muted">Henüz evrak eklenmemiş</p>
            </div>
          ) : (
            <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
              {documents.map((doc, idx) => (
                <div
                  key={doc.id}
                  className="px-3 py-2"
                  style={{ borderBottom: idx < documents.length - 1 ? '1px solid #e9ecef' : 'none' }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2" style={{ flex: 1, minWidth: 0 }}>
                      <i className={`${documentTypeIcons[doc.documentType] || 'ri-file-line'} text-primary`} style={{ fontSize: '1.2rem' }}></i>
                      <div style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center gap-2">
                          <span className="small" style={{ fontWeight: 500 }}>
                            {documentTypeLabels[doc.documentType] || doc.documentType}
                          </span>
                          {doc.expiryDate && isExpired(doc.expiryDate) && (
                            <span className="badge bg-danger" style={{ fontSize: '0.6rem' }}>Süresi Dolmuş</span>
                          )}
                          {doc.expiryDate && isExpiringSoon(doc.expiryDate) && (
                            <span className="badge bg-warning text-dark" style={{ fontSize: '0.6rem' }}>Süresi Dolacak</span>
                          )}
                        </div>
                        <div className="text-muted small text-truncate">
                          {doc.description || doc.fileName}
                          <span className="ms-2">({formatFileSize(doc.fileSize)})</span>
                        </div>
                        {doc.expiryDate && (
                          <div className="text-muted small">
                            <i className="ri-calendar-line me-1"></i>
                            Son: {formatDate(doc.expiryDate)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-1 ms-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-0 text-primary"
                        onClick={() => openAuthFile(doc.filePath)}
                        title="Görüntüle / İndir"
                      >
                        <i className="ri-eye-line"></i>
                      </button>
                      {isAdmin && (
                        <button
                          type="button"
                          className="btn btn-sm btn-link p-0 text-danger"
                          onClick={() => handleDeleteDocument(doc.id, documentTypeLabels[doc.documentType] || doc.fileName)}
                          title="Sil"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Usage History Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }} className="rounded-3 p-3 mb-3">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              Kullanım Geçmişi
            </span>
            <span className="text-muted small">{usageHistory.length} kayıt</span>
          </div>

          {loadingHistory ? (
            <div className="text-center py-4">
              <Spinner size="sm" color="primary" />
              <p className="mb-0 mt-2 text-muted small">Yükleniyor...</p>
            </div>
          ) : usageHistory.length === 0 ? (
            <div className="text-center py-4 rounded-3" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
              <i className="ri-calendar-todo-line text-muted" style={{ fontSize: '2rem' }}></i>
              <p className="mb-0 mt-2 text-muted">Kullanım kaydı bulunamadı</p>
            </div>
          ) : (
            <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7', maxHeight: '300px', overflowY: 'auto' }}>
              {usageHistory.map((entry, idx) => (
                <div
                  key={entry.id}
                  className="px-3 py-2"
                  style={{ borderBottom: idx < usageHistory.length - 1 ? '1px solid #e9ecef' : 'none' }}
                >
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <span className="text-primary small" style={{ fontWeight: 500 }}>
                      <i className="ri-calendar-line me-1"></i>
                      {formatDate(entry.date)}
                    </span>
                    <span className="badge bg-white text-dark border" style={{ fontSize: '0.7rem' }}>
                      {getUsageTypeLabel(entry.usageType)}
                    </span>
                  </div>
                  <div className="small">
                    {entry.site ? (
                      <span>
                        <strong>{entry.site.city}</strong> / {entry.site.project} / {entry.site.name}
                      </span>
                    ) : (
                      <span className="text-muted">Şantiye bilgisi yok</span>
                    )}
                  </div>
                  {entry.driver && (
                    <div className="text-muted small mt-1">
                      <i className="ri-user-line me-1"></i>
                      {entry.driver}
                    </div>
                  )}
                  {entry.km != null && (
                    <div className="small mt-1" style={{ color: '#405189' }}>
                      <i className="ri-route-line me-1"></i>
                      {entry.km} km
                    </div>
                  )}
                  {entry.hours != null && (
                    <div className="small mt-1" style={{ color: '#405189' }}>
                      <i className="ri-time-line me-1"></i>
                      {Number(entry.hours)} saat
                    </div>
                  )}
                  {entry.notes && (
                    <div className="text-muted small mt-1 fst-italic">
                      <i className="ri-sticky-note-line me-1"></i>
                      {entry.notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </SideModal>
  );
};

export default VehicleDetailModal;
